import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Target, BookOpen, Award, CheckCircle2, BarChart3 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import {  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const overallStats = [
  { label: 'Course Progress', value: 62, icon: BookOpen, color: 'text-blue-500', bg: 'bg-blue-100' },
  { label: 'Assignments', value: 75, icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-100' },
  { label: 'Quiz Avg Score', value: 86, icon: Target, color: 'text-primary', bg: 'bg-primary/15' },
  { label: 'Attendance', value: 90, icon: Award, color: 'text-purple-500', bg: 'bg-purple-100' },
];

const weeklyData = [
  { day: 'Mon', hours: 2.5 },
  { day: 'Tue', hours: 1.8 },
  { day: 'Wed', hours: 3.2 },
  { day: 'Thu', hours: 2.0 },
  { day: 'Fri', hours: 2.8 },
  { day: 'Sat', hours: 4.1 },
  { day: 'Sun', hours: 1.5 },
];

const moduleProgress = [
  { module: 'Module 1: Intro to GenAI', progress: 100, status: 'completed' },
  { module: 'Module 2: LLMs & Transformers', progress: 100, status: 'completed' },
  { module: 'Module 3: Prompt Engineering', progress: 75, status: 'in-progress' },
  { module: 'Module 4: API Integration', progress: 30, status: 'in-progress' },
  { module: 'Module 5: RAG & Vector DBs', progress: 0, status: 'upcoming' },
  { module: 'Module 6: Capstone Project', progress: 0, status: 'upcoming' },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass rounded-xl border border-white/80 px-3 py-2 shadow-lg">
        <p className="text-xs font-bold">{label}</p>
        <p className="text-xs text-primary font-semibold">{payload[0].value}h study time</p>
      </div>
    );
  }
  return null;
};

export default function PerformanceSection() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Performance & Progress</h2>
        <p className="text-muted-foreground text-sm mt-1">Your learning analytics at a glance</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {overallStats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
            whileHover={{ y: -3, transition: { duration: 0.2 } }}
            className="glass rounded-2xl border border-white/80 p-5 relative overflow-hidden group">
            <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center mb-3`}>
              <s.icon className={`w-5 h-5 ${s.color}`} />
            </div>
            <div className="flex items-end gap-1 mb-2">
              <motion.span
                className="font-heading font-black text-3xl text-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.08 + 0.3 }}
              >
                {s.value}
              </motion.span>
              <span className="text-lg font-bold text-muted-foreground mb-0.5">%</span>
            </div>
            <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
            <div className="mt-2.5">
              <Progress value={s.value} className="h-1.5" />
            </div>
            {/* Background accent */}
            <motion.div
              className="absolute -bottom-4 -right-4 w-16 h-16 rounded-full opacity-0 group-hover:opacity-10 transition-opacity duration-300"
              style={{ background: 'hsl(var(--primary))' }}
            />
          </motion.div>
        ))}
      </div>

      {/* Weekly Study Chart */}
      <div className="glass rounded-2xl border border-white/80 p-6">
        <h3 className="font-heading font-bold text-base mb-5 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          Weekly Study Hours
        </h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weeklyData} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fontWeight: 600, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} unit="h" />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(254,210,2,0.08)', radius: 8 }} />
              <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Module Progress */}
      <div className="glass rounded-2xl border border-white/80 p-6">
        <h3 className="font-heading font-bold text-base mb-5 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Module Completion
        </h3>
        <div className="space-y-4">
          {moduleProgress.map((m, i) => (
            <motion.div key={m.module} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold font-heading truncate flex-1 pr-3">{m.module}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    m.status === 'completed' ? 'bg-green-100 text-green-700' :
                    m.status === 'in-progress' ? 'bg-primary/15 text-primary' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {m.status === 'in-progress' ? 'In Progress' : m.status === 'completed' ? '✓ Done' : 'Upcoming'}
                  </span>
                  <span className="text-xs font-bold text-foreground w-8 text-right">{m.progress}%</span>
                </div>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${m.progress}%` }}
                  transition={{ duration: 0.8, delay: i * 0.1 + 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className={`h-full rounded-full ${
                    m.status === 'completed' ? 'bg-green-500' :
                    m.status === 'in-progress' ? 'bg-gradient-to-r from-primary to-yellow-400' :
                    'bg-muted-foreground/20'
                  }`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}